import { zodResolver } from "@hookform/resolvers/zod";
import { useDocumentTitle } from "@uidotdev/usehooks";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { z } from "zod";

import Button from "@/components/Button";
import FormField from "@/components/FormField";
import FormFields from "@/components/FormFields";
import Header from "@/components/Header";
import { Input } from "@/components/Input";
import Loader from "@/components/Loader";
import { useNotification } from "@/components/Notification";
import { ErrorMessages } from "@/constants/messages";
import { PageRoute, PageTitle } from "@/constants/pages";
import { useForgotEmailMutation } from "@/hooks/user";
import { isSetAndNotEmpty } from "@/utils/helpers";

const FormSchema = z.object({
  phone: z.string().min(10, "Phone is required"),
});

type FormSchema = z.infer<typeof FormSchema>;

export function RecoverAccountPage() {
  const location = useLocation();
  const phone = location.state?.phone as string | undefined;
  const forgotEmailMutation = useForgotEmailMutation();
  const navigate = useNavigate();
  const notify = useNotification();
  const [showSuccess, setShowSuccess] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { isValid },
  } = useForm<FormSchema>({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      phone: phone ?? "",
    },
  });

  useDocumentTitle(PageTitle.AUTH_RECOVER_ACCOUNT);

  if (!isSetAndNotEmpty(phone)) {
    return <Navigate to={PageRoute.AUTH} />;
  }

  function onGoToSignIn() {
    navigate(PageRoute.AUTH, { replace: true });
  }

  const onSubmit = handleSubmit((data) => {
    forgotEmailMutation.mutate(data.phone, {
      onSuccess: (result) => {
        if (result.success) {
          setShowSuccess(true);
        } else {
          notify(ErrorMessages.GENERIC, "error");
        }
      },
    });
  });

  if (showSuccess) {
    return (
      <div className={"flex flex-col"}>
        <Header className="mb-5">Check your Text Messages!</Header>

        <div className={"flex flex-col gap-4"}>
          <span className={"font-sans"}>
            We&apos;ve sent a text with account recovery details to the phone
            number provided. Please check your text messages for the email
            address associated with your account.
          </span>

          <Button onClick={onGoToSignIn}>Sign in</Button>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} className="mb-8">
      <Header>Recover Account Details</Header>

      <p className={"mb-5 font-sans"}>
        We recognized the phone number in our system.
      </p>

      <FormFields>
        <FormField>
          <Input
            id="phone"
            type="phone"
            autoComplete="phone"
            {...register("phone")}
            disabled
          />
        </FormField>
        <span className={"text-center font-sans"}>
          A recovery text will be sent to the phone number provided
        </span>
        {forgotEmailMutation.isPending ? (
          <Loader />
        ) : (
          <Button type="submit" disabled={!isValid}>
            Text Recovery Details
          </Button>
        )}
      </FormFields>
    </form>
  );
}
